import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

export default function SearchableSelect({ value, options = [], placeholder = 'Select an option', searchPlaceholder = 'Search', onChange, disabled = false }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef(null)
  const searchRef = useRef(null)
  const filteredOptions = options.filter(option => option.toLowerCase().includes(query.trim().toLowerCase()))

  useEffect(() => {
    if (!open) return undefined
    const closeOutside = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', closeOutside)
    return () => document.removeEventListener('mousedown', closeOutside)
  }, [open])

  useEffect(() => {
    if (open) requestAnimationFrame(() => searchRef.current?.focus())
    else setQuery('')
  }, [open])

  const select = option => {
    onChange(option)
    setOpen(false)
  }

  return <div ref={rootRef} className={`relative ${open ? 'z-40' : ''}`}>
    <button type="button" disabled={disabled} onClick={() => setOpen(previous => !previous)} aria-haspopup="listbox" aria-expanded={open} className={`flex h-11 w-full items-center justify-between rounded-lg border bg-white px-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 ${open ? 'border-purple-500 ring-1 ring-purple-500' : 'border-gray-300 hover:border-gray-400'}`}>
      <span className={value ? 'font-medium text-gray-800' : 'text-gray-500'}>{value || placeholder}</span>
      <ChevronDown className={`h-4 w-4 shrink-0 text-gray-600 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>

    {open && <div className="absolute left-0 right-0 top-full mt-1.5 overflow-hidden rounded-lg border border-gray-200 bg-white p-2 shadow-xl" role="listbox">
      <input ref={searchRef} value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => event.key === 'Escape' && setOpen(false)} placeholder={searchPlaceholder} className="mb-2 h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-700 outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
      <div className="max-h-56 overflow-y-auto">
        <button type="button" onClick={() => select('')} className={`mb-1 w-full rounded-md px-3 py-2.5 text-left text-sm ${!value ? 'bg-purple-100 text-purple-600' : 'text-gray-600 hover:bg-gray-50'}`}>{placeholder}</button>
        {filteredOptions.map(option => <button key={option} type="button" role="option" aria-selected={option === value} onClick={() => select(option)} className={`mb-1 w-full rounded-md px-3 py-2.5 text-left text-sm last:mb-0 ${option === value ? 'bg-purple-100 text-purple-600' : 'text-gray-700 hover:bg-gray-50'}`}>{option}</button>)}
        {filteredOptions.length === 0 && <p className="px-3 py-4 text-center text-sm text-gray-400">No venues found</p>}
      </div>
    </div>}
  </div>
}
